import React, { memo, useCallback } from 'react';
import { Handle, Position } from 'reactflow';
import { Paper, Typography, Box, alpha, Chip } from '@mui/material';
import { Email } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

const EmailNode = memo(({ data, selected, id }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Node color - Teal theme
  const nodeColor = theme.palette.info.dark;
  const textColor = theme.palette.common.white;
  const borderColor = selected ? theme.palette.secondary.main : 'transparent';

  const handleDoubleClick = useCallback((event) => {
    event.stopPropagation();
    const editEvent = new CustomEvent('editNode', { detail: { nodeId: id } });
    window.dispatchEvent(editEvent);
  }, [id]);

  const handleContextMenu = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    const contextEvent = new CustomEvent('nodeContextMenu', {
      detail: {
        nodeId: id,
        x: event.clientX,
        y: event.clientY
      }
    });
    window.dispatchEvent(contextEvent);
  }, [id]);

  const formatEmailList = (emails) => {
    if (Array.isArray(emails)) {
      return emails.length > 2 ? `${emails.slice(0, 2).join(', ')} +${emails.length - 2}` : emails.join(', ');
    }
    return emails;
  };

  return (
    <Paper
      elevation={selected ? 12 : 4}
      onDoubleClick={handleDoubleClick}
      onContextMenu={handleContextMenu}
      sx={{
        padding: 2.5,
        borderRadius: 3,
        minWidth: 200,
        minHeight: 100,
        position: 'relative',
        overflow: 'visible',
        cursor: 'move',
        background: isDark
          ? `linear-gradient(145deg, ${alpha('#0D2E4D', 0.9)}, ${alpha('#0D2E4D', 0.8)})`
          : `linear-gradient(145deg, ${alpha(nodeColor, 0.95)}, ${alpha(nodeColor, 0.85)})`,
        backdropFilter: 'blur(10px)',
        border: selected
          ? `2px solid ${theme.palette.secondary.main}`
          : `1px solid ${alpha(nodeColor, 0.3)}`,
        transition: theme.transitions.create([
          'transform', 'box-shadow', 'border'
        ], {
          duration: theme.transitions.duration.shorter,
        }),
        '&:hover': {
          transform: 'translateY(-4px) scale(1.02)',
          boxShadow: theme.shadows[16],
          border: `1px solid ${alpha(theme.palette.secondary.main, 0.5)}`,
          '& .email-icon': {
            transform: 'rotateY(180deg)',
          }
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          borderRadius: 'inherit',
          background: `radial-gradient(circle at top right, ${alpha(theme.palette.warning.main, 0.1)}, transparent)`,
          pointerEvents: 'none',
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={`Email node: ${data.label || 'Send Email'}`}
      aria-selected={selected}
    >
      {/* Handles */}
      <Handle
        type="target"
        position={Position.Top}
        style={{
          background: `linear-gradient(135deg, ${nodeColor} 0%, ${theme.palette.info.dark} 100%)`,
          border: `2px solid ${theme.palette.background.paper}`,
          width: 14,
          height: 14,
          boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.2)}`,
        }}
      />

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          p: 1.5,
          mx: -1,
          mt: -1,
          borderRadius: 2,
          background: alpha(nodeColor, 0.15),
          backdropFilter: 'blur(8px)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{
            p: 0.5,
            borderRadius: 1.5,
            background: `linear-gradient(135deg, ${nodeColor} 0%, ${theme.palette.info.dark} 100%)`,
            display: 'flex',
            color: theme.palette.common.white,
            boxShadow: `0 2px 8px ${alpha(nodeColor, 0.3)}`,
          }}>
            <Email className="email-icon" sx={{ fontSize: 18, transition: 'transform 0.5s ease' }} />
          </Box>
          <Typography variant="subtitle1" fontWeight="600" color={textColor}>
            {data.label || 'Send Email'}
          </Typography>
        </Box>
      </Box>

      {/* Description */}
      {data.description && (
        <Typography
          variant="body2"
          sx={{
            mb: 2,
            color: alpha(textColor, 0.85),
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: 1.4,
          }}
        >
          {data.description}
        </Typography>
      )}

      {/* Email Fields */}
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
        {data.to && (
          <Chip
            label={`To: ${formatEmailList(data.to).length > 20 ? formatEmailList(data.to).substring(0, 20) + '...' : formatEmailList(data.to)}`}
            size="small"
            sx={{
              height: 22,
              fontSize: '0.7rem',
              backgroundColor: alpha(theme.palette.info.main, 0.2),
              color: theme.palette.info.dark,
              border: `1px solid ${alpha(theme.palette.info.main, 0.4)}`,
            }}
          />
        )}
        {data.subject && (
          <Chip
            label={data.subject.length > 20 ? `${data.subject.substring(0, 20)}...` : data.subject}
            size="small"
            sx={{
              height: 22,
              fontSize: '0.7rem',
              backgroundColor: alpha(theme.palette.warning.main, 0.2),
              color: theme.palette.warning.dark,
              border: `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
            }}
          />
        )}
        {data.template && (
          <Chip
            label={`Template: ${data.template}`}
            size="small"
            sx={{
              height: 22,
              fontSize: '0.7rem',
              backgroundColor: alpha(theme.palette.secondary.main, 0.2),
              color: theme.palette.secondary.dark,
              border: `1px solid ${alpha(theme.palette.secondary.main, 0.4)}`,
            }}
          />
        )}
      </Box>

      {/* Additional Email Info */}
      {(data.cc || data.bcc || data.attachments) && (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
          {data.cc && (
            <Chip
              label={`CC: ${Array.isArray(data.cc) ? data.cc.length : 1}`}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                backgroundColor: alpha(theme.palette.warning.main, 0.2),
                color: theme.palette.warning.dark,
                border: `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
              }}
            />
          )}
          {data.bcc && (
            <Chip
              label={`BCC: ${Array.isArray(data.bcc) ? data.bcc.length : 1}`}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                backgroundColor: alpha(theme.palette.grey[600], 0.2),
                color: theme.palette.grey[700],
                border: `1px solid ${alpha(theme.palette.grey[600], 0.4)}`,
              }}
            />
          )}
          {data.attachments && (
            <Chip
              label={`${Array.isArray(data.attachments) ? data.attachments.length : 1} files`}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                backgroundColor: alpha(theme.palette.grey[500], 0.2),
                color: theme.palette.grey[700],
                border: `1px solid ${alpha(theme.palette.grey[500], 0.4)}`,
              }}
            />
          )}
        </Box>
      )}

      {/* Priority Indicator */}
      {data.priority && (
        <Box
          sx={{
            mt: 1,
            p: 0.5,
            backgroundColor: alpha(
              data.priority === 'high' ? theme.palette.error.main :
              data.priority === 'medium' ? theme.palette.warning.main : theme.palette.success.main,
              0.2
            ),
            borderRadius: 1,
            border: `1px solid ${alpha(
              data.priority === 'high' ? theme.palette.error.main :
              data.priority === 'medium' ? theme.palette.warning.main : theme.palette.success.main,
              0.4
            )}`,
            textAlign: 'center',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: data.priority === 'high' ? theme.palette.error.dark :
                     data.priority === 'medium' ? theme.palette.warning.dark : theme.palette.success.dark,
              fontSize: '0.7rem',
              fontWeight: 600,
              textTransform: 'uppercase',
            }}
          >
            {data.priority} Priority
          </Typography>
        </Box>
      )}

      {/* Status Indicator */}
      <Box
        sx={{
          position: 'absolute',
          top: -8,
          right: -8,
          width: 24,
          height: 24,
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${nodeColor} 0%, ${theme.palette.info.dark} 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: theme.palette.common.white,
          boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.2)}`,
          border: `2px solid ${theme.palette.background.paper}`,
        }}
      >
        <Email sx={{ fontSize: 14 }} />
      </Box>

      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          background: `linear-gradient(135deg, ${nodeColor} 0%, ${theme.palette.info.dark} 100%)`,
          border: `2px solid ${theme.palette.background.paper}`,
          width: 14,
          height: 14,
          boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.2)}`,
        }}
      />
    </Paper>
  );
});

EmailNode.displayName = 'EmailNode';

export default EmailNode;